/**
 * Connect big screen (§15.2 #2, §15.8): the host scans the screen's pairing QR (the same scanner
 * as Take hand) or types its 6-character code, and sends `pairScreen`. More than one screen can
 * be connected. A pairing QR scanned with the phone's own camera opens `/pair/CODE`, which comes
 * back here as `/play/ROOM?pair=CODE` with the code filled in.
 */
import { SHORT_CODE_ALPHABET } from "@handshake/protocol/constants";
import { useEffect, useState, type FormEvent } from "react";
import { Scanner } from "../../components/Scanner.tsx";
import { Sheet } from "../../components/Sheet.tsx";
import { send, toast } from "../../lib/store.ts";

const CODE = new RegExp(`^[${SHORT_CODE_ALPHABET}]{6}$`);

/** The pairing code in a scanned QR (a `/pair/CODE` link) or typed text, or null. */
export function pairingCodeFrom(text: string): string | null {
  const trimmed = text.trim();
  const fromUrl = /\/pair\/([A-Za-z0-9]{6})\/?(?:[?#].*)?$/.exec(trimmed)?.[1];
  const code = (fromUrl ?? trimmed).toUpperCase();
  return CODE.test(code) ? code : null;
}

/** A code handed over in the URL (`?pair=CODE`), removed from the URL once read. */
function takePairParam(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const code = params.get("pair");
  if (!code) return null;
  params.delete("pair");
  const rest = params.toString();
  window.history.replaceState(null, "", window.location.pathname + (rest ? `?${rest}` : ""));
  return pairingCodeFrom(code);
}

export function ConnectScreenButton({ className = "btn block" }: { className?: string }) {
  const [initial] = useState(takePairParam);
  const [open, setOpen] = useState(initial !== null);
  return (
    <>
      <button
        type="button"
        className={className}
        data-testid="connect-screen"
        onClick={() => setOpen(true)}
      >
        Connect big screen
      </button>
      <Sheet
        open={open}
        title="Connect big screen"
        onClose={() => setOpen(false)}
        testId="connect-screen-sheet"
      >
        <ConnectScreen initialCode={initial} onDone={() => setOpen(false)} />
      </Sheet>
    </>
  );
}

function ConnectScreen({
  initialCode,
  onDone,
}: {
  initialCode: string | null;
  onDone: () => void;
}) {
  const [mode, setMode] = useState<"scan" | "type">(initialCode ? "type" : "scan");
  const [code, setCode] = useState(initialCode ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const pair = async (raw: string) => {
    const c = pairingCodeFrom(raw);
    if (!c) {
      setError("That is not a big-screen code. It has 6 letters and digits.");
      return;
    }
    if (busy) return;
    setBusy(true);
    setError(null);
    const ack = await send({ type: "pairScreen", code: c }, { quiet: true });
    setBusy(false);
    if (ack.ok) {
      toast("Big screen connected.");
      onDone();
    } else {
      setError(ack.error?.message ?? "The screen could not be connected.");
    }
  };

  useEffect(() => {
    setError(null);
  }, [mode]);

  return (
    <div className="stack">
      <p className="muted" style={{ margin: 0 }}>
        On the TV or laptop, open this site and choose <strong>Big screen</strong>. Scan the code it
        shows, or type it. The screen shows only what every phone shows.
      </p>
      {mode === "scan" ? (
        <>
          <Scanner onResult={(text) => void pair(text)} />
          <button
            type="button"
            className="btn block"
            data-testid="screen-type-code"
            onClick={() => setMode("type")}
          >
            Type code instead
          </button>
        </>
      ) : (
        <form
          className="stack"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            void pair(code);
          }}
        >
          <label className="field">
            <span>Screen code</span>
            <input
              className="text code"
              name="screen-code"
              data-testid="screen-code-input"
              autoComplete="off"
              autoCapitalize="characters"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
            />
          </label>
          <button
            type="submit"
            className="btn primary block"
            data-testid="screen-code-submit"
            disabled={busy}
          >
            Connect
          </button>
          <button type="button" className="btn block ghost" onClick={() => setMode("scan")}>
            Scan instead
          </button>
        </form>
      )}
      {error ? (
        <p className="error-text" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
